import * as H from 'history';
import React, {useState} from 'react';
import {BiSliderAlt} from 'react-icons/bi';
import {IoCaretForwardOutline} from 'react-icons/io5';
import {useHistory} from 'react-router-dom';
import {HSDoseRound, HSFacilityGroup, ReasonCode} from 'server-openapi';
import styled from 'styled-components';
import {useCurrentUser} from '../../../../core/authn/UserProvider';
import {DateUtils} from '../../../../core/utils/dateUtils';
import {Button} from '../../../../kit/Button';
import {SearchInput} from '../../../../kit/Forms/SearchInput';
import {Select as FormSelect} from '../../../../kit/Forms/Select';
import Select, {components, MultiValue} from 'react-select';
import {Grid} from '../../../../kit/Grid';
import {useApiUtils} from '../../../../syncstream/utils/hooks/useApiUtils';
import {useRoundSchedule} from '../../../Rounds/services/RoundScheduleProvider';
import {FilterDrugTypeControlled, FilterDrugTypeOther,} from '../DashboardPage';
import {SelectWards} from "./SelectWards";
import {useGroupPermissions} from "../../../../core/authz/PermissionsProvider";

interface IProps {
  facilityGroup?: HSFacilityGroup;
  searchQuery: string;
  onSearch: (query: string) => void;
  hasDosesInWindow: boolean;
  onSelectFacilities: (facilities: string[]) => void;
  selectedFacilityIds?: string[];
  selectedFacilityNames?: string[];
  filterDrugTypeControlled?: FilterDrugTypeControlled;
  filterDrugTypeOther?: FilterDrugTypeOther;
  onSelectDrugTypeControlled: (drugTypeControlled: FilterDrugTypeControlled) => void;
  onSelectDrugTypeOther: (drugTypeOther: FilterDrugTypeOther | undefined) => void;
  selectedDoseStatus?: ReasonCode;
  onSelectDoseStatus: (doseStatus: ReasonCode) => void;
}

function CreateRoundButton(props: {
  history: H.History<H.LocationState>;
  hasDosesInWindow: boolean;
  activeRound?: HSDoseRound;
  facilityGroupId?: number;
}) {
  const buttonText = props.activeRound ? 'Resume Round' : 'Create Round';
  const roundScheduleContext = useRoundSchedule();
  const groupPermissions = useGroupPermissions();
  const canStartRound = groupPermissions.canUndertakeMedicationRound;
  const canViewRound = groupPermissions.canViewMedicationRound;

  return (
    <Button
      data-testid="create-round-button"
      large
      onClick={() => {
        if (props.activeRound) {
          roundScheduleContext.set(props.activeRound);
          props.history.push(`/facility-group/${props.facilityGroupId}/rounds/${props.activeRound.clinicalSystemId}`);
          return;
        }
        if (!props.hasDosesInWindow) {
          // TODO: Do a pop-up
          alert('There are no residents with medication to administer within the round window');
        } else {
          props.history.push(`/facility-group/${props.facilityGroupId}/rounds/create`);
        }
      }}
      disabled={!canStartRound && !canViewRound}
      fullWidth
    >
      {buttonText} <IoCaretForwardOutline />
    </Button>
  );
}

export function DashboardMenu(props: IProps) {
  const user = useCurrentUser();
  const history = useHistory();
  const groupPermissions = useGroupPermissions();
  // const [isFilterVisible, setFilterVisible] = useState(!!props.selectedFacilityNames || !!props.filterDrugTypeControlled || !!props.filterDrugTypeOther);
  const [isFilterVisible, setFilterVisible] = useState(!!props.selectedFacilityIds || !!props.filterDrugTypeControlled || !!props.filterDrugTypeOther);
  const facilityUtils = useApiUtils().facilities;
  const roundUtils = useApiUtils().rounds;
  const facilityGroupUtils = useApiUtils().facilityGroups;
  const facilities = facilityUtils.fetchAllFacilitiesByUniqueMsName(props.facilityGroup);
  // const facilities = facilityUtils.fetchAllFacilities(props.facilityGroup);
  const wingLabel = props.facilityGroup?.hsId
    ? facilityGroupUtils.getFacilityUILabel(props.facilityGroup?.hsId)
    : 'wings';
    const residentLabel = props.facilityGroup?.hsId
    ? facilityGroupUtils.getResidentUILabel(props.facilityGroup.hsId)
    : 'Resident';

  const activeRounds = roundUtils
    /* eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain */
    .activeUserRounds(user.profile.sub, props.facilityGroup?.hsId!)
    .sort((l, r) => DateUtils.compareDateStringsDescending(l.createdAt, r.createdAt));
  const convertMultiValueOptionToEnum = (options: MultiValue<any>): FilterDrugTypeOther | undefined => {
    if (options.length === 0) {
      return undefined;
    }

    if (options.length === 1) {
      return options[0].value;
    }
    return options.map(x => x.value).reduce((a, b) => {
      return (a | b);
    });
  }

  const controlledDrugsTypeOptions = [
    { label: 'Controlled', value: FilterDrugTypeControlled.Controlled },
    { label: 'Non-controlled', value: FilterDrugTypeControlled.NonControlled }
  ];

  // If you can't administer everything, then show the 'administrable' option.
  if (!roundUtils.canAdministerEverything(groupPermissions))
  {
    controlledDrugsTypeOptions.push({ label: 'Administrable', value: FilterDrugTypeControlled.Administrable });
  }

  const medicationTypeOptions = [
    { value: FilterDrugTypeOther.TimeCritical, label: "Time Critical" },
    { value: FilterDrugTypeOther.Insulin, label: "Insulin" },
    { value: FilterDrugTypeOther.Patch, label: "Patch" },
    { value: FilterDrugTypeOther.Injection, label: "Injection" }
  ];

  const allOptions: any[] = [{
      label: "Medication Type",
      options: medicationTypeOptions,
      value: -1
    }];

  return (
    <Container>
      <Grid cols={1} gap={1} style={{ flex: 1 }}>
        <ResidentFilterContainer cols={2} colsMobile={1} gap={1}>
          <CreateRoundButton
            history={history}
            hasDosesInWindow={props.hasDosesInWindow}
            facilityGroupId={props.facilityGroup?.hsId}
            activeRound={activeRounds[0]}
          />
          <SearchInput
            onChange={(_, v) => props.onSearch(v)}
            value={props.searchQuery}
            name="search"
            placeholder={`Search ${residentLabel}`}
          />
        </ResidentFilterContainer>
        {isFilterVisible && (
           <Grid colsTemplate='auto auto auto auto' gap={1} style={{ zIndex: 20}}>
             <FormSelect
                 data-testid="select-drugs"
                 fullWidth
                 placeholder="All Medications"
                 name="control"
                 options={controlledDrugsTypeOptions}
                 onChange={(_, v) => {
                   props.onSelectDrugTypeControlled(v);
                 }}
                 value={props.filterDrugTypeControlled}
             />
             <Select
                 aria-label="Filter By"
                 isMulti={true}
                 isClearable={true}
                 escapeClearsValue={true}
                 name={`FilterDrugTypeOther`}
                 placeholder={`Filter By: `}
                 options={allOptions}
                 onChange={(option, actionMeta) => {
                   const optionEnum = convertMultiValueOptionToEnum(option);
                   props.onSelectDrugTypeOther(
                     optionEnum
                   );
                 }}

                 components={{
                   Option: (props) => {
                     return (
                         <div>
                           <components.Option { ...props }>
                             <input type='checkbox' checked={props.isSelected} onChange={() => null}/>{' '}
                             <label>{props.label}</label>
                           </components.Option>
                         </div>
                     );
                   },
                 }}
                 defaultValue={medicationTypeOptions.filter(v =>
                     (props.filterDrugTypeOther ?
                         (v.value | FilterDrugTypeOther.TimeCritical) === props.filterDrugTypeOther ||
                         (v.value | FilterDrugTypeOther.Insulin) === props.filterDrugTypeOther ||
                         (v.value | FilterDrugTypeOther.Patch) === props.filterDrugTypeOther ||
                         (v.value | FilterDrugTypeOther.Injection) === props.filterDrugTypeOther : undefined))}
                 hideSelectedOptions={false}
                 closeMenuOnSelect={false}
                 styles={{
                   control: (provided) => ({ ...provided, width: '100%', height: '100%', zIndex:9999}),
                   menu: provided =>({...provided, zIndex:9999})
                 }}
             />

             <SelectWards wards={facilities} selectedWardIds={props.selectedFacilityIds} wingLabel={wingLabel}
                          selectionChanged={(facilityIds) => props.onSelectFacilities(facilityIds)} />
             {/* <SelectWards wards={facilities} selectedWardNames={props.selectedFacilityNames} wingLabel={wingLabel}*/}
             {/*              selectionChanged={(facilityNames) => props.onSelectFacilities(facilityNames)} />*/}
              <FormSelect
                  fullWidth
                  placeholder="All"
                  name="refused"
                  options={[
                    { label: 'Administered Late', value: ReasonCode.DosedLate },
                    { label: 'Refused', value: ReasonCode.Refused },
                    { label: 'Withheld', value: ReasonCode.Withheld },
                  ]}
                  value={props.selectedDoseStatus}
                  onChange={(_, value) => props.onSelectDoseStatus(value)}
              />
            </Grid>
        )}
      </Grid>
      <IconContainer onClick={() => setFilterVisible(!isFilterVisible)}>
        <BiSliderAlt />
      </IconContainer>
    </Container>
  );
}

const IconContainer = styled.h2`
  cursor: pointer;
  padding: 0.5em;
  color: ${(props) => props.theme.backgrounds.default.fg};
`;

const Container = styled.div`
  display: flex;
  padding: 0.5em 2em;
`;

const ResidentFilterContainer = styled(Grid)`
  grid-template-rows: min-content;
  button {
    padding: 0;
  }
`;
